/**
 * Shared URL Query Parameter Parsing Utilities
 *
 * This file contains utility functions for parsing URL query parameters.
 * Used by filters hooks to parse URL params into typed values.
 */

/**
 * Parse a comma-separated string into an array
 * @param value - The string value to parse (e.g., "value1,value2,value3")
 * @returns Array of non-empty strings
 * @example
 * parseArray("apple,banana,orange") // ["apple", "banana", "orange"]
 * parseArray("") // []
 * parseArray(null) // []
 */
export const parseArray = (value: string | null): string[] => {
	if (!value) return [];
	return value.split(',').filter(Boolean);
};

/**
 * Parse a boolean value from string
 * @param value - The string value to parse (e.g., "true" or "false")
 * @returns Boolean value (true only if value === "true")
 * @example
 * parseBoolean("true") // true
 * parseBoolean("false") // false
 * parseBoolean("anything") // false
 * parseBoolean(null) // false
 */
export const parseBoolean = (value: string | null): boolean => {
	return value === 'true';
};

/**
 * Parse a number from string with a default fallback
 * @param value - The string value to parse
 * @param defaultValue - Default value to return if parsing fails
 * @returns Parsed integer or default value
 * @example
 * parseNumber("42", 10) // 42
 * parseNumber("invalid", 10) // 10
 * parseNumber(null, 10) // 10
 * parseNumber("", 10) // 10
 */
export const parseNumber = (value: string | null, defaultValue: number): number => {
	if (!value) return defaultValue;
	const parsed = parseInt(value, 10);
	return isNaN(parsed) ? defaultValue : parsed;
};
